import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { BarChart3, RefreshCcw, AlertCircle, TrendingDown, Filter, Bell, Eraser, CheckCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Friendly labels for each funnel step. Keep in sync with the backend
// FUNNEL_STEP_ORDER list and the frontend FUNNEL_STEPS constants.
const STEP_LABELS = {
  landing_view: 'Landing page view',
  cta_click: 'CTA click',
  preview_view: 'Preview page view',
  file_upload: 'File upload',
  generation_start: 'Generation started',
  generation_complete: 'Generation completed',
  signup_prompt_shown: 'Signup prompt shown',
  signup_complete: 'Signup completed',
  download_click: 'Download click',
  download_complete: 'Download completed',
};

const FunnelDashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [days, setDays] = useState(7);
  const [includeInternal, setIncludeInternal] = useState(false);
  // Admin action state — shared by the two action buttons below.
  const [actionLoading, setActionLoading] = useState('');
  const [actionResult, setActionResult] = useState(null);

  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        `${API}/api/admin/funnel-stats?days=${days}&include_internal=${includeInternal}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(`Failed to load funnel stats (${res.status})`);
      const json = await res.json();
      setData(json);
    } catch (e) {
      setError(e.message || 'Failed to load funnel stats');
    } finally {
      setLoading(false);
    }
  }, [token, days, includeInternal]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  // Trigger the daily drop-off alert check on demand. `force=true`
  // bypasses the once-per-day idempotency so we can validate email
  // delivery without waiting until tomorrow.
  const runAlertCheck = async (force = true) => {
    setActionLoading('alert');
    setActionResult(null);
    try {
      const res = await fetch(
        `${API}/api/admin/funnel-alerts/check?force=${force}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Alert check failed');
      const degraded = json.degraded || [];
      setActionResult({
        ok: true,
        kind: 'alert',
        msg: degraded.length === 0
          ? `No regressions found for ${json.day_key || 'yesterday'}. ${json.status === 'already_ran' ? '(Already ran today — re-run with force.)' : ''}`
          : `${degraded.length} step${degraded.length === 1 ? '' : 's'} degraded — ${json.emails_sent || 0} email(s) sent.`,
      });
    } catch (e) {
      setActionResult({ ok: false, kind: 'alert', msg: e.message || 'Alert check failed' });
    } finally {
      setActionLoading('');
    }
  };

  // Wipe the preview-quota record so the operator can re-test the full
  // upload→generate→unlock flow from the same browser. Defaults to
  // caller's IP server-side; we also clear the local-storage / cookie
  // markers the PreviewPage uses to short-circuit the limit check.
  const resetPreviewQuota = async () => {
    if (!window.confirm('Reset preview quota for your IP? This lets you re-run the full demo flow once.')) return;
    setActionLoading('reset');
    setActionResult(null);
    try {
      const res = await fetch(`${API}/api/admin/preview/reset-quota`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({}),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Reset failed');
      // Clear the client-side gate so the page lets us upload again.
      try {
        localStorage.removeItem('pm_preview_used');
        document.cookie = 'pm_preview=;max-age=0;path=/';
      } catch (_) {}
      setActionResult({
        ok: true,
        kind: 'reset',
        msg: `Quota reset — ${json.deleted} record${json.deleted === 1 ? '' : 's'} deleted${json.ip ? ` for IP ${json.ip}` : ''}. Local cache also cleared.`,
      });
    } catch (e) {
      setActionResult({ ok: false, kind: 'reset', msg: e.message || 'Reset failed' });
    } finally {
      setActionLoading('');
    }
  };

  if (user?.role !== 'admin') return null;

  const topCount = data?.steps?.[0]?.unique_sessions || 0;

  return (
    <div className="max-w-5xl" data-testid="funnel-dashboard">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <BarChart3 className="w-7 h-7 text-indigo-400" />
            <h1 className="text-2xl sm:text-3xl font-bold text-white">Conversion Funnel</h1>
          </div>
          <p className="text-text-secondary text-sm">
            Server-side funnel data — immune to ad-blockers and browser tracking protection.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Window selector */}
          <div className="flex items-center gap-1 bg-bg-light border border-text-muted/15 rounded-lg p-1" data-testid="funnel-window-selector">
            {[1, 7, 30].map(d => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-3 py-1 rounded text-xs font-semibold transition ${
                  days === d ? 'bg-primary text-white' : 'text-text-secondary hover:text-text-primary'
                }`}
                data-testid={`funnel-window-${d}d`}
              >
                {d === 1 ? '24h' : `${d}d`}
              </button>
            ))}
          </div>

          {/* Internal toggle */}
          <label
            className="flex items-center gap-1.5 px-3 py-1.5 bg-bg-light border border-text-muted/15 rounded-lg text-xs text-text-secondary cursor-pointer hover:border-primary/30"
            data-testid="funnel-include-internal-toggle"
          >
            <Filter className="w-3.5 h-3.5" />
            <input
              type="checkbox"
              checked={includeInternal}
              onChange={(e) => setIncludeInternal(e.target.checked)}
              className="accent-primary"
            />
            Include internal
          </label>

          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-bg-light border border-text-muted/15 rounded-lg text-xs text-text-secondary hover:border-primary/30 disabled:opacity-50"
            data-testid="funnel-refresh-btn"
          >
            <RefreshCcw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-300 text-sm flex items-center gap-2" data-testid="funnel-error">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      {/* KPI strip */}
      {data && (
        <div className="grid sm:grid-cols-3 gap-3 mb-6" data-testid="funnel-kpis">
          <div className="bg-bg-light rounded-xl border border-text-muted/15 p-4">
            <p className="text-xs uppercase tracking-wider text-text-muted mb-1">Unique landing visitors</p>
            <p className="text-2xl font-black text-white">{topCount.toLocaleString()}</p>
            <p className="text-[11px] text-text-muted mt-1">last {data.days} day{data.days === 1 ? '' : 's'}</p>
          </div>
          <div className="bg-bg-light rounded-xl border border-green-500/20 p-4">
            <p className="text-xs uppercase tracking-wider text-text-muted mb-1">Landing → Download</p>
            <p className="text-2xl font-black text-green-400" data-testid="funnel-conversion-pct">
              {topCount === 0 ? '—' : `${data.conversion_pct_landing_to_download}%`}
            </p>
            <p className="text-[11px] text-text-muted mt-1">
              {topCount === 0 ? 'no landing events yet' : 'end-to-end conversion rate'}
            </p>
          </div>
          <div className="bg-bg-light rounded-xl border border-text-muted/15 p-4">
            <p className="text-xs uppercase tracking-wider text-text-muted mb-1">Data source</p>
            <p className="text-sm font-semibold text-white">Server-side log</p>
            <p className="text-[11px] text-text-muted mt-1">
              {includeInternal ? 'Including internal-user events' : 'Excluding internal-user events'}
            </p>
          </div>
        </div>
      )}

      {/* Admin actions — alert delivery test + preview-quota reset */}
      <div className="grid sm:grid-cols-2 gap-3 mb-6" data-testid="funnel-admin-actions">
        <div className="bg-bg-light rounded-xl border border-text-muted/15 p-4 flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
            <Bell className="w-5 h-5 text-amber-300" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-text-primary mb-0.5">Drop-off alerts</p>
            <p className="text-xs text-text-muted mb-2.5">
              Daily check that emails admins when any step degrades by &gt; 15 pp vs the prior day.
            </p>
            <button
              onClick={() => runAlertCheck(true)}
              disabled={actionLoading === 'alert'}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/15 border border-amber-500/30 hover:bg-amber-500/25 text-amber-300 rounded-lg text-xs font-semibold transition disabled:opacity-50"
              data-testid="funnel-run-alert-check-btn"
            >
              {actionLoading === 'alert' ? <RefreshCcw className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />}
              Run check now (force)
            </button>
          </div>
        </div>

        <div className="bg-bg-light rounded-xl border border-text-muted/15 p-4 flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/15 border border-primary/30 flex items-center justify-center flex-shrink-0">
            <Eraser className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-text-primary mb-0.5">Reset my preview quota</p>
            <p className="text-xs text-text-muted mb-2.5">
              Clears the 1-per-IP free preview gate for your current IP so you can re-run the full demo end to end.
            </p>
            <button
              onClick={resetPreviewQuota}
              disabled={actionLoading === 'reset'}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary/15 border border-primary/30 hover:bg-primary/25 text-primary rounded-lg text-xs font-semibold transition disabled:opacity-50"
              data-testid="funnel-reset-quota-btn"
            >
              {actionLoading === 'reset' ? <RefreshCcw className="w-3.5 h-3.5 animate-spin" /> : <Eraser className="w-3.5 h-3.5" />}
              Reset for my IP
            </button>
          </div>
        </div>
      </div>

      {actionResult && (
        <div
          className={`mb-4 p-3 rounded-lg text-sm flex items-start gap-2 ${
            actionResult.ok
              ? 'bg-green-500/10 border border-green-500/20 text-green-300'
              : 'bg-red-500/10 border border-red-500/20 text-red-300'
          }`}
          data-testid={`funnel-action-result-${actionResult.kind}`}
        >
          {actionResult.ok ? <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> : <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />}
          <span>{actionResult.msg}</span>
        </div>
      )}

      {/* Funnel rows */}
      <div className="bg-bg-light rounded-2xl border border-text-muted/15 overflow-hidden" data-testid="funnel-table">
        <div className="grid grid-cols-12 px-4 py-3 bg-bg-dark/40 text-[11px] uppercase tracking-wider text-text-muted font-semibold">
          <div className="col-span-5">Step</div>
          <div className="col-span-2 text-right">Unique sessions</div>
          <div className="col-span-2 text-right">Events</div>
          <div className="col-span-3 text-right">Drop-off vs prev</div>
        </div>

        {!data && !loading && !error && (
          <p className="p-6 text-sm text-text-muted text-center">No data yet.</p>
        )}
        {loading && !data && (
          <p className="p-6 text-sm text-text-muted text-center">Loading...</p>
        )}

        {data?.steps?.map((s, idx) => {
          const widthPct = topCount > 0 ? Math.max(2, (s.unique_sessions / topCount) * 100) : 0;
          const drop = s.drop_off_pct_from_prev;
          const dropTone = drop === null ? 'text-text-muted'
            : drop >= 50 ? 'text-red-400'
            : drop >= 20 ? 'text-amber-400'
            : 'text-green-400';

          return (
            <div
              key={s.step}
              className="relative grid grid-cols-12 px-4 py-3 items-center border-t border-text-muted/10"
              data-testid={`funnel-row-${s.step}`}
            >
              {/* Background bar — visual width = relative to top step */}
              <div
                className="absolute left-0 top-0 bottom-0 bg-primary/5"
                style={{ width: `${widthPct}%` }}
                aria-hidden="true"
              />
              <div className="relative col-span-5 flex items-center gap-2">
                <span className="inline-flex w-6 h-6 rounded-full bg-bg-dark/60 items-center justify-center text-[10px] font-bold text-text-muted">
                  {idx + 1}
                </span>
                <span className="text-sm font-medium text-text-primary">{STEP_LABELS[s.step] || s.step}</span>
              </div>
              <div className="relative col-span-2 text-right text-sm font-bold text-white tabular-nums">
                {s.unique_sessions.toLocaleString()}
              </div>
              <div className="relative col-span-2 text-right text-sm text-text-secondary tabular-nums">
                {s.events.toLocaleString()}
              </div>
              <div className={`relative col-span-3 text-right text-sm font-semibold tabular-nums ${dropTone}`}>
                {drop === null ? (
                  <span className="text-text-muted">—</span>
                ) : (
                  <span className="inline-flex items-center gap-1 justify-end">
                    {drop > 0 && <TrendingDown className="w-3.5 h-3.5" />}
                    {drop}%
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-text-muted mt-4">
        Tip: large drop-offs (red) flag the steps to optimize first. A healthy
        landing → preview funnel typically drops &lt; 30% per step.
      </p>
    </div>
  );
};

export default FunnelDashboard;
